import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Smartphone, Link2, Unlink, Ban, Trash2, History } from "lucide-react";
import { api, errMsg, formatTime } from "@/lib/api";

const STATUS = {
  available: ["Tersedia", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  assigned: ["Dipakai", "bg-sky-50 text-sky-700 border-sky-200"],
  blocked: ["Diblokir", "bg-rose-50 text-rose-700 border-rose-200"],
};

function AddNumberForm({ onAdded }) {
  const [form, setForm] = useState({ number: "", device: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/wa-numbers", form); toast.success("Nomor ditambahkan"); setForm({ number: "", device: "", note: "" }); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-5 grid gap-3 sm:grid-cols-[1fr_1fr_1.4fr_auto] items-end" data-testid="wa-add-form">
      <label className="text-xs text-slate-500">Nomor WA (milik platform)<input className="input-soft h-10 mt-1" placeholder="0812xxxxxxx" value={form.number} onChange={(e) => set("number", e.target.value)} required inputMode="tel" data-testid="wa-add-number" /></label>
      <label className="text-xs text-slate-500">HP utama<input className="input-soft h-10 mt-1" placeholder="mis. HP-02 (slot A)" value={form.device} onChange={(e) => set("device", e.target.value)} data-testid="wa-add-device" /></label>
      <label className="text-xs text-slate-500">Catatan<input className="input-soft h-10 mt-1" placeholder="opsional" value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="wa-add-note" /></label>
      <button className="btn-primary h-10 px-4" data-testid="wa-add-submit">Tambah nomor</button>
    </form>
  );
}

function AssignControl({ n, talents, onDone }) {
  const [talentId, setTalentId] = useState("");
  const assign = async () => {
    if (!talentId) return toast.error("Pilih talent dulu");
    try { const { data } = await api.post(`/admin/wa-numbers/${n.id}/assign`, { talent_id: talentId }); toast.success(`+${n.number} ditetapkan ke ${data.talent_name}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (talents.length === 0) return <span className="text-xs text-slate-400">Semua talent sudah punya nomor</span>;
  return (
    <div className="flex gap-2 items-center">
      <select className="input-soft h-9 text-sm w-44" value={talentId} onChange={(e) => setTalentId(e.target.value)} data-testid={`wa-assign-select-${n.id}`}>
        <option value="">Pilih talent…</option>
        {talents.map((t) => <option key={t.id} value={t.id}>{t.name}{t.status === "pending" ? " (menunggu)" : ""}</option>)}
      </select>
      <button className="btn-primary h-9 px-3 text-sm" onClick={assign} data-testid={`wa-assign-button-${n.id}`}><Link2 size={14} strokeWidth={1.5} />Tetapkan</button>
    </div>
  );
}

function ReleaseControl({ n, onDone, onCancel }) {
  const [loggedOut, setLoggedOut] = useState(false);
  const [note, setNote] = useState("");
  const release = async () => {
    try { await api.post(`/admin/wa-numbers/${n.id}/release`, { logged_out: loggedOut, note }); toast.success(`+${n.number} dilepas dari ${n.talent_name}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3 text-sm" data-testid={`wa-release-form-${n.id}`}>
      <p className="text-amber-900">Lepas nomor dari <strong>{n.talent_name}</strong>. Talent otomatis offline dan tidak bisa menerima order sampai dapat nomor baru.</p>
      <label className="flex items-start gap-2 text-slate-700 cursor-pointer"><input type="checkbox" className="mt-0.5 accent-[#6B8E7B]" checked={loggedOut} onChange={(e) => setLoggedOut(e.target.checked)} data-testid={`wa-release-confirm-${n.id}`} />Saya sudah menekan <em>Keluar dari perangkat</em> untuk HP talent ini di HP utama ({n.device || "HP utama"}).</label>
      <input className="input-soft h-9" placeholder="Alasan / catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`wa-release-note-${n.id}`} />
      <div className="flex gap-2"><button className="btn-primary h-9 px-3 text-sm" onClick={release} disabled={!loggedOut} data-testid={`wa-release-submit-${n.id}`}>Lepas nomor</button><button className="btn-ghost h-9 text-sm" onClick={onCancel}>Batal</button></div>
    </div>
  );
}

function NumberRow({ n, talents, reload }) {
  const [releasing, setReleasing] = useState(false);
  const [label, tone] = STATUS[n.status] || [n.status, ""];
  const patch = async (body, msg) => { try { await api.patch(`/admin/wa-numbers/${n.id}`, body); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { if (!window.confirm(`Hapus +${n.number} dari inventaris?`)) return; try { await api.delete(`/admin/wa-numbers/${n.id}`); toast.success("Nomor dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const past = (n.history || []).filter((h) => h.released_at);
  return (
    <div className="card-soft p-5" data-testid={`wa-number-${n.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-semibold text-slate-800 tracking-wide" data-testid={`wa-number-value-${n.id}`}>+{n.number}</p>
            <span className={`text-xs px-2.5 py-1 rounded-full border ${tone}`} data-testid={`wa-number-status-${n.id}`}>{label}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><Smartphone size={12} strokeWidth={1.5} />{n.device || "HP utama belum dicatat"}{n.note ? ` · ${n.note}` : ""}</p>
          {n.status === "assigned" && <p className="text-sm text-slate-700 mt-2" data-testid={`wa-number-talent-${n.id}`}>Dipakai <strong>{n.talent_name}</strong> sejak {formatTime(n.assigned_at)}</p>}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {n.status === "available" && <AssignControl n={n} talents={talents} onDone={reload} />}
          {n.status === "assigned" && !releasing && <button className="btn-outline h-9 px-3 text-sm" onClick={() => setReleasing(true)} data-testid={`wa-release-button-${n.id}`}><Unlink size={14} strokeWidth={1.5} />Lepas</button>}
          {n.status === "available" && <button className="btn-ghost h-9 text-sm text-rose-700 hover:bg-rose-50" onClick={() => patch({ status: "blocked" }, "Nomor diblokir")} data-testid={`wa-block-${n.id}`}><Ban size={14} strokeWidth={1.5} />Blokir</button>}
          {n.status === "blocked" && <button className="btn-outline h-9 px-3 text-sm" onClick={() => patch({ status: "available" }, "Nomor tersedia lagi")} data-testid={`wa-unblock-${n.id}`}>Buka blokir</button>}
          {n.status !== "assigned" && <button className="btn-ghost h-9 text-sm" onClick={remove} data-testid={`wa-delete-${n.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>}
        </div>
      </div>
      {releasing && <ReleaseControl n={n} onDone={() => { setReleasing(false); reload(); }} onCancel={() => setReleasing(false)} />}
      {past.length > 0 && (
        <details className="mt-3 text-xs text-slate-500" data-testid={`wa-history-${n.id}`}>
          <summary className="cursor-pointer inline-flex items-center gap-1"><History size={12} strokeWidth={1.5} />Riwayat pemakaian ({past.length})</summary>
          <ul className="mt-2 space-y-1">{past.map((h, i) => <li key={i}>{h.talent_name}: {formatTime(h.assigned_at)} → {formatTime(h.released_at)}{h.note ? ` · ${h.note}` : ""}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

export function WaNumbersTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/wa-numbers").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-slate-500">Memuat...</p>;
  const counts = data.numbers.reduce((a, n) => ({ ...a, [n.status]: (a[n.status] || 0) + 1 }), {});
  return (
    <div className="space-y-5" data-testid="wa-numbers-tab">
      <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="wa-intro-note">
        <p className="font-medium">Semua nomor WhatsApp milik Sudut Nadi. Talent hanya menautkan HP-nya sebagai perangkat pendamping.</p>
        <p className="mt-1">Alur: tambah nomor → tetapkan ke talent → di HP utama buka WhatsApp › Perangkat tertaut › Tautkan perangkat, pindai dari HP talent. Untuk mencabut akses: tekan <em>Keluar dari perangkat</em> di HP utama, lalu klik <em>Lepas</em> di sini. Nomor saat ini masih dummy.</p>
      </div>
      <div className="flex gap-3 flex-wrap text-sm" data-testid="wa-counts">
        {Object.entries(STATUS).map(([k, [label, tone]]) => <span key={k} className={`px-3 py-1.5 rounded-full border ${tone}`} data-testid={`wa-count-${k}`}>{label}: {counts[k] || 0}</span>)}
        {data.unassigned_talents.length > 0 && <span className="px-3 py-1.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200" data-testid="wa-count-unassigned">Talent tanpa nomor: {data.unassigned_talents.length}</span>}
      </div>
      <AddNumberForm onAdded={load} />
      <div className="space-y-3">
        {data.numbers.length === 0 && <p className="text-slate-500 text-sm" data-testid="wa-empty">Belum ada nomor di inventaris.</p>}
        {data.numbers.map((n) => <NumberRow key={n.id} n={n} talents={data.unassigned_talents} reload={load} />)}
      </div>
    </div>
  );
}
